import { create } from 'zustand'

import {
  DEFAULT_PRODUCT_SORT_DIRECTION,
  PRODUCTS_PAGE_SIZE,
} from '@/features/products/constants'
import type {
  ProductListFilter,
  ProductSortField,
} from '@/features/products/types/product.types'
import type { SortDirection } from '@/types/common.types'

export type ProductListViewKey = 'products' | 'checkout'

export interface ProductListViewState {
  page: number
  limit: number
  search: string
  filters: ProductListFilter
  sortBy: ProductSortField
  sortDirection: SortDirection
}

interface ProductsState {
  views: Record<ProductListViewKey, ProductListViewState>
}

interface ProductsActions {
  setPage: (viewKey: ProductListViewKey, page: number) => void
  setSearch: (viewKey: ProductListViewKey, search: string) => void
  setCategory: (viewKey: ProductListViewKey, category?: string) => void
  setBrand: (viewKey: ProductListViewKey, brand?: string) => void
  setExternalId: (viewKey: ProductListViewKey, externalId?: string) => void
  setSortBy: (viewKey: ProductListViewKey, sortBy: ProductSortField) => void
  setSortDirection: (
    viewKey: ProductListViewKey,
    sortDirection: SortDirection,
  ) => void
}

function updateFilter(
  filters: ProductListFilter,
  key: keyof ProductListFilter,
  value?: string,
): ProductListFilter {
  const nextFilters = { ...filters, [key]: value || undefined }

  if (!value) {
    delete nextFilters[key]
  }

  return nextFilters
}

function createInitialViewState(): ProductListViewState {
  return {
    page: 1,
    limit: PRODUCTS_PAGE_SIZE,
    search: '',
    filters: {},
    sortBy: 'name',
    sortDirection: DEFAULT_PRODUCT_SORT_DIRECTION,
  }
}

const initialState: ProductsState = {
  views: {
    products: createInitialViewState(),
    checkout: createInitialViewState(),
  },
}

export const useProductsStore = create<ProductsState & ProductsActions>()((set) => ({
  ...initialState,

  setPage: (viewKey, page) => {
    set((state) => ({
      views: {
        ...state.views,
        [viewKey]: {
          ...state.views[viewKey],
          page: Math.max(1, page),
        },
      },
    }))
  },

  setSearch: (viewKey, search) => {
    set((state) => ({
      views: {
        ...state.views,
        [viewKey]: {
          ...state.views[viewKey],
          page: 1,
          search,
        },
      },
    }))
  },

  setCategory: (viewKey, category) => {
    set((state) => ({
      views: {
        ...state.views,
        [viewKey]: {
          ...state.views[viewKey],
          page: 1,
          filters: updateFilter(state.views[viewKey].filters, 'category', category),
        },
      },
    }))
  },

  setBrand: (viewKey, brand) => {
    set((state) => ({
      views: {
        ...state.views,
        [viewKey]: {
          ...state.views[viewKey],
          page: 1,
          filters: updateFilter(state.views[viewKey].filters, 'brand', brand),
        },
      },
    }))
  },

  setExternalId: (viewKey, externalId) => {
    set((state) => ({
      views: {
        ...state.views,
        [viewKey]: {
          ...state.views[viewKey],
          page: 1,
          filters: updateFilter(
            state.views[viewKey].filters,
            'externalId',
            externalId,
          ),
        },
      },
    }))
  },

  setSortBy: (viewKey, sortBy) => {
    set((state) => ({
      views: {
        ...state.views,
        [viewKey]: {
          ...state.views[viewKey],
          page: 1,
          sortBy,
        },
      },
    }))
  },

  setSortDirection: (viewKey, sortDirection) => {
    set((state) => ({
      views: {
        ...state.views,
        [viewKey]: {
          ...state.views[viewKey],
          page: 1,
          sortDirection,
        },
      },
    }))
  },
}))
